import { useEffect, useState } from "react";

/**
 * True when the visitor has asked for no motion — either through the OS
 * (`prefers-reduced-motion`) or through the accessibility widget's "Pause
 * animations" toggle, which sets `data-a11y-motion="1"` on <html>.
 *
 * The widget's CSS collapses animation and transition durations, but react-slick
 * autoplay is a setInterval that CSS cannot reach: without this hook the
 * carousels keep advancing every 7s, just instantly instead of smoothly, which
 * is worse than leaving them alone. Any component that starts motion on its own
 * should consult this.
 *
 * Starts false so the server-rendered markup and the first client render agree;
 * the real value lands in the mount effect.
 */
export default function useMotionPaused() {
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    const root = document.documentElement;
    const media =
      typeof window.matchMedia === "function"
        ? window.matchMedia("(prefers-reduced-motion: reduce)")
        : null;

    const read = () =>
      setPaused(
        root.getAttribute("data-a11y-motion") === "1" ||
          Boolean(media && media.matches)
      );

    read();

    const observer = new MutationObserver(read);
    observer.observe(root, {
      attributes: true,
      attributeFilter: ["data-a11y-motion"],
    });

    // Safari < 14 only has the deprecated addListener.
    if (media) {
      if (media.addEventListener) media.addEventListener("change", read);
      else if (media.addListener) media.addListener(read);
    }

    return () => {
      observer.disconnect();
      if (media) {
        if (media.removeEventListener)
          media.removeEventListener("change", read);
        else if (media.removeListener) media.removeListener(read);
      }
    };
  }, []);

  return paused;
}
